// src/components/EditInvoiceModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "../api/axios";
import {
  FileText,
  CalendarDays,
  DollarSign,
  Percent,
  Info,
  Copy,
  Minus,
  ArrowUp,
  ArrowDown,
  Sparkles,
} from "lucide-react";

// ✅ safe currency utilities
import { formatMoney, safeCurrency } from "../utils/money";

/* ----------------------------- UI helpers ----------------------------- */
// Slightly more compact inputs
const INPUT_BASE =
  "block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-[0.9rem] leading-6 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500";
const LABEL_BASE = "block text-[12.5px] font-medium text-gray-700";
const HINT_BASE = "text-[12px] text-gray-500";

function LabeledInput({
  id,
  name,
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  required = false,
  autoComplete,
  rightAdornment,
  onClick,
  inputMode,
  pattern,
}) {
  const hasRight = !!rightAdornment;
  return (
    <div className="space-y-1.5">
      {label ? (
        <label htmlFor={id || name} className={LABEL_BASE}>
          {label} {required ? <span className="text-red-500">*</span> : null}
        </label>
      ) : null}
      <div className="relative">
        <input
          id={id || name}
          name={name}
          value={value}
          onChange={onChange}
          onClick={onClick}
          placeholder={placeholder}
          type={type}
          inputMode={inputMode}
          pattern={pattern}
          autoComplete={autoComplete}
          className={[INPUT_BASE, hasRight ? "pr-10" : ""].filter(Boolean).join(" ")}
          required={required}
        />
        {hasRight ? (
          <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-gray-400">
            {rightAdornment}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function LabeledCheckbox({ label, checked, onChange }) {
  return (
    <label className="inline-flex items-center gap-2 text-[12.5px] text-gray-700">
      <input type="checkbox" checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}

function Section({ icon: Icon, title, hint, children }) {
  return (
    <div className="border rounded-xl p-5 space-y-4 shadow-sm bg-white">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 text-gray-700">
          <Icon size={18} />
        </div>
        <div>
          <div className="text-gray-800 font-semibold">{title}</div>
          {hint ? <div className={HINT_BASE}>{hint}</div> : null}
        </div>
      </div>
      {children}
    </div>
  );
}

/* ----------------------------- math helpers (server-aligned) ----------------------------- */
const round2 = (n) => Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100;
const clamp = (val, min, max) => Math.min(Math.max(Number(val || 0), min), max);

/**
 * calcLine
 * - subtotal: qty * base rate BEFORE discount (tax-exclusive)
 * - lineDiscount: total discount amount for this line
 * - taxAmount: tax on discounted base
 * - total: subtotal - lineDiscount + taxAmount
 *
 * This matches the full-page editor where:
 *   Subtotal shows 500, Line discounts 50, Tax 0, Total 450
 *   for qty=1, rate=500, disc%=10, tax%=0.
 */
function calcLine(li) {
  const qty = Math.max(0, Number(li.quantity || 0));
  if (!qty) {
    return { subtotal: 0, lineDiscount: 0, taxAmount: 0, total: 0 };
  }

  const rateIn = li.rate ?? li.unitPrice ?? 0;
  const rate = Math.max(0, Number(rateIn) || 0);

  const taxPct = clamp(li.taxPercent, 0, 100);
  const dPct = clamp(li.discountPercent, 0, 100);
  const dAmt = Math.max(0, Number(li.discountAmount || 0));
  const taxIncl = !!li.isTaxInclusive;

  // Base unit price before tax
  let unitBase = rate;
  if (taxIncl && taxPct > 0) {
    unitBase = rate / (1 + taxPct / 100);
  }

  const subtotal = round2(qty * unitBase);

  // Per-unit discount: amount wins over percent
  let perUnitDiscount = 0;
  if (dAmt > 0) {
    perUnitDiscount = dAmt;
  } else if (dPct > 0) {
    perUnitDiscount = unitBase * (dPct / 100);
  }
  // Clamp so you can’t discount below zero
  perUnitDiscount = Math.min(Math.max(perUnitDiscount, 0), unitBase);

  const lineDiscount = round2(qty * perUnitDiscount);

  const discountedUnit = unitBase - perUnitDiscount;
  const discountedBase = round2(qty * discountedUnit);

  // Tax on discounted base
  let taxAmount = 0;
  if (taxPct > 0) {
    taxAmount = round2(discountedBase * (taxPct / 100));
  }

  const total = round2(discountedBase + taxAmount);

  return { subtotal, lineDiscount, taxAmount, total };
}

/**
 * computeTotals
 * - subtotal: sum of line subtotals (before any discounts)
 * - lineDiscountTotal: sum of all line discounts
 * - taxTotal: sum of all line tax amounts
 * - totalAmount: subtotal - lineDiscountTotal + taxTotal
 *
 * NOTE: Invoice-level discount is intentionally NOT used here
 *       (we removed that concept from the UI per current spec).
 */
function computeTotals(lineItems) {
  let subtotal = 0;
  let lineDiscountTotal = 0;
  let taxTotal = 0;

  for (const li of lineItems) {
    const c = calcLine(li);
    subtotal += c.subtotal;
    lineDiscountTotal += c.lineDiscount;
    taxTotal += c.taxAmount;
  }

  subtotal = round2(subtotal);
  lineDiscountTotal = round2(lineDiscountTotal);
  taxTotal = round2(taxTotal);

  const totalAmount = round2(subtotal - lineDiscountTotal + taxTotal);

  return {
    subtotal,
    lineDiscountTotal,
    taxTotal,
    totalAmount,
  };
}

// ✅ safe money formatter
const money = (n, c = "USD") => formatMoney(n, safeCurrency(c));

/* ----------------------------- component ----------------------------- */
export default function EditInvoiceModal({ isOpen, onClose, invoice, invoiceId, onUpdated }) {
  const formRef = useRef();

  // fetch state (when only invoiceId is given)
  const [fetched, setFetched] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");

  const inv = invoice || fetched;
  const needFetch = !invoice && !!invoiceId;

  // basics
  const [issueDate, setIssueDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [notes, setNotes] = useState("");
  const [lineItems, setLineItems] = useState([]);

  /* ----------------------------- data loading ----------------------------- */
  useEffect(() => {
    if (!isOpen) {
      setFetched(null);
      setFetchError("");
      setLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (invoice) {
      hydrateFromInvoice(invoice);
      return;
    }

    if (needFetch) {
      let cancelled = false;
      setLoading(true);
      setFetchError("");
      setFetched(null);

      api
        .get(`/invoices/${invoiceId}`)
        .then((res) => {
          if (cancelled) return;
          const data = res?.data?.data || res?.data || null;
          if (!data?._id) {
            setFetchError("Invoice not found");
            setFetched(null);
          } else {
            setFetched(data);
            hydrateFromInvoice(data);
          }
        })
        .catch((e) => {
          if (cancelled) return;
          setFetchError(e?.response?.data?.message || "Failed to load invoice");
          setFetched(null);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });

      return () => {
        cancelled = true;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, invoiceId, needFetch, invoice]);

  useEffect(() => {
    if (isOpen && invoice) hydrateFromInvoice(invoice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice, isOpen]);

  function hydrateFromInvoice(src) {
    setIssueDate(src.issueDate ? src.issueDate.slice(0, 10) : "");
    setDueDate(src.dueDate ? src.dueDate.slice(0, 10) : "");
    setCurrency((src.currency || "USD").toUpperCase());
    setNotes(src.notes || "");

    const lines = Array.isArray(src.lineItems) ? src.lineItems : [];
    setLineItems(
      lines.map((li) => ({
        description: li.description || li.name || "",
        quantity:
          typeof li.quantity === "number" ? li.quantity : Number(li.quantity || 0),
        rate:
          typeof li.rate === "number"
            ? li.rate
            : typeof li.unitPrice === "number"
            ? li.unitPrice
            : Number(li.rate || li.unitPrice || 0),
        unitPrice:
          typeof li.unitPrice === "number"
            ? li.unitPrice
            : typeof li.rate === "number"
            ? li.rate
            : Number(li.unitPrice || li.rate || 0),
        taxPercent: clamp(li.taxPercent, 0, 100),
        discountPercent: clamp(li.discountPercent, 0, 100),
        discountAmount: Number(li.discountAmount || 0),
        isTaxInclusive: !!li.isTaxInclusive,
        showDiscount:
          Number(li.discountAmount) > 0 || Number(li.discountPercent) > 0,
      }))
    );
  }

  /* ----------------------------- derived ----------------------------- */
  const totals = useMemo(() => computeTotals(lineItems), [lineItems]);
  const invalidDueDate =
    issueDate && dueDate ? new Date(dueDate) < new Date(issueDate) : false;

  /* ----------------------------- line handlers ----------------------------- */
  const patchLine = (i, field, val) =>
    setLineItems((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: val };
      if (field === "rate" || field === "unitPrice") {
        const num = Number(val || 0);
        next[i].rate = num;
        next[i].unitPrice = num;
      }
      return next;
    });

  const addLine = () =>
    setLineItems((prev) => [
      ...prev,
      {
        description: "",
        quantity: 1,
        rate: 0,
        unitPrice: 0,
        taxPercent: 0,
        discountPercent: 0,
        discountAmount: 0,
        isTaxInclusive: false,
        showDiscount: false,
      },
    ]);

  const removeLine = (i) =>
    setLineItems((prev) => prev.filter((_, idx) => idx !== i));

  const duplicateLine = (i) =>
    setLineItems((prev) => {
      const copy = { ...prev[i] };
      return [...prev.slice(0, i + 1), copy, ...prev.slice(i + 1)];
    });

  const moveLine = (i, dir) =>
    setLineItems((prev) => {
      const next = [...prev];
      const ni = i + dir;
      if (ni < 0 || ni >= next.length) return prev;
      [next[i], next[ni]] = [next[ni], next[i]];
      return next;
    });

  /* ----------------------------- submit ----------------------------- */
  const submit = async ({ closeAfter = false } = {}) => {
    const id = inv?._id;
    if (!id) return;

    if (invalidDueDate) {
      toast.error("Due date must be on or after Issue date.");
      return;
    }
    const hasMeaningful = lineItems.some(
      (li) =>
        Number(li.quantity) > 0 &&
        (Number(li.rate) > 0 || Number(li.discountAmount) > 0)
    );
    if (!hasMeaningful) {
      toast.error("Add at least one line with a positive quantity and rate.");
      return;
    }

    const payload = {
      issueDate: issueDate || undefined,
      dueDate: dueDate || undefined,
      currency: (currency || "USD").toUpperCase(),
      notes,
      // ❌ No invoice-level discount in this editor anymore
      lineItems: lineItems.map((li) => {
        const rateValue = Number(li.rate ?? li.unitPrice ?? 0);
        return {
          description: li.description || "",
          quantity: Number(li.quantity || 0),
          rate: rateValue,
          unitPrice: rateValue,
          taxPercent: clamp(li.taxPercent, 0, 100),
          discountPercent: clamp(li.discountPercent, 0, 100),
          discountAmount: Number(li.discountAmount || 0),
          isTaxInclusive: !!li.isTaxInclusive,
        };
      }),
    };

    try {
      await api.put(`/invoices/${id}`, payload);
      toast.success("Invoice updated");
      onUpdated?.();
      if (closeAfter) onClose?.();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to update invoice";
      toast.error(msg);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      {/* Hide native date picker icons to avoid the “shadow calendar” */}
      <style>{`
        input[type="date"]::-webkit-inner-spin-button{display:none;}
        input[type="date"]::-webkit-clear-button{display:none;}
        input[type="date"]::-webkit-calendar-picker-indicator{opacity:0;}
      `}</style>

      <div className="relative bg-white w-full max-w-6xl rounded-2xl shadow-2xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-t-2xl">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-14 h-14 rounded-xl bg-white/20 border border-white/30 flex items-center justify-center shrink-0">
              <Sparkles className="text-white" size={22} />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-semibold truncate">
                Edit Invoice {inv?.invoiceNumber ? `#${inv.invoiceNumber}` : ""}
              </h2>
              <div className="text-xs text-white/85 mt-1">
                Make quick corrections to dates, currency, notes, and line items.
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="bg-white/20 hover:bg-white/30 rounded-full p-2"
            type="button"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Loading / Error states */}
        {needFetch && loading ? (
          <div className="flex-1 p-8 bg-gray-50/50 flex items-center justify-center">
            <div className="text-sm text-gray-600">Loading invoice…</div>
          </div>
        ) : fetchError ? (
          <div className="flex-1 p-8 bg-gray-50/50 flex items-center justify-center">
            <div className="text-sm text-red-600">{fetchError}</div>
          </div>
        ) : (
          <>
            {/* Body */}
            <form
              ref={formRef}
              onSubmit={(e) => {
                e.preventDefault();
                submit({ closeAfter: false });
              }}
              className="flex-1 overflow-y-auto p-8 space-y-8 bg-gray-50/50"
            >
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Dates & Currency */}
                  <Section
                    icon={CalendarDays}
                    title="Dates & Currency"
                    hint="Issue date, due date, and invoice currency"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <LabeledInput
                        name="issueDate"
                        label="Issue Date"
                        type="date"
                        value={issueDate}
                        onChange={(e) => setIssueDate(e.target.value)}
                        onClick={(e) => e.target.showPicker?.()}
                        rightAdornment={<CalendarDays size={16} />}
                      />
                      <LabeledInput
                        name="dueDate"
                        label="Due Date"
                        type="date"
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                        onClick={(e) => e.target.showPicker?.()}
                        rightAdornment={<CalendarDays size={16} />}
                      />
                      <LabeledInput
                        name="currency"
                        label="Currency"
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                        placeholder="USD"
                      />
                    </div>
                    {invalidDueDate ? (
                      <div className="text-[12px] text-red-600 mt-1">
                        Due date must be on or after the Issue date.
                      </div>
                    ) : null}
                  </Section>

                  {/* Line Items */}
                  <Section
                    icon={DollarSign}
                    title="Items & Services"
                    hint="Edit quantities, rates, taxes, and discounts"
                  >
                    <div className="space-y-4">
                      {lineItems.map((li, i) => {
                        const c = calcLine(li);
                        const hasDiscount =
                          Number(li.discountAmount) > 0 ||
                          Number(li.discountPercent) > 0;

                        return (
                          <div
                            key={i}
                            className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div className="text-sm font-semibold text-gray-700">
                                Line {i + 1}
                              </div>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  className="p-2 rounded-lg border hover:bg-gray-50"
                                  title="Move up"
                                  onClick={() => moveLine(i, -1)}
                                >
                                  <ArrowUp size={16} />
                                </button>
                                <button
                                  type="button"
                                  className="p-2 rounded-lg border hover:bg-gray-50"
                                  title="Move down"
                                  onClick={() => moveLine(i, 1)}
                                >
                                  <ArrowDown size={16} />
                                </button>
                                <button
                                  type="button"
                                  className="p-2 rounded-lg border hover:bg-gray-50"
                                  title="Duplicate"
                                  onClick={() => duplicateLine(i)}
                                >
                                  <Copy size={16} />
                                </button>
                                <button
                                  type="button"
                                  className="p-2 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50"
                                  title="Remove"
                                  onClick={() => removeLine(i)}
                                >
                                  <Minus size={16} />
                                </button>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                              <LabeledInput
                                label="Qty"
                                type="number"
                                value={li.quantity}
                                onChange={(e) =>
                                  patchLine(i, "quantity", Number(e.target.value))
                                }
                              />
                              {/* Rate – text input so we can control decimals */}
                              <LabeledInput
                                label="Rate"
                                type="text"
                                inputMode="decimal"
                                pattern="^[0-9]*[.]?[0-9]*$"
                                value={String(li.rate ?? "")}
                                onChange={(e) => patchLine(i, "rate", e.target.value)}
                                rightAdornment={<DollarSign size={14} />}
                              />

                              <div className="col-span-2">
                                {li.showDiscount ? (
                                  <div className="grid grid-cols-2 gap-3">
                                    <LabeledInput
                                      label="Disc %"
                                      type="text"
                                      inputMode="decimal"
                                      pattern="^[0-9]*[.]?[0-9]*$"
                                      value={String(li.discountPercent ?? "")}
                                      onChange={(e) =>
                                        patchLine(
                                          i,
                                          "discountPercent",
                                          Number(e.target.value)
                                        )
                                      }
                                      rightAdornment={<Percent size={14} />}
                                    />
                                    <LabeledInput
                                      label="Disc $"
                                      type="text"
                                      inputMode="decimal"
                                      pattern="^[0-9]*[.]?[0-9]*$"
                                      value={String(li.discountAmount ?? "")}
                                      onChange={(e) =>
                                        patchLine(
                                          i,
                                          "discountAmount",
                                          Number(e.target.value)
                                        )
                                      }
                                      rightAdornment={<DollarSign size={14} />}
                                    />
                                    <div className="col-span-2">
                                      <button
                                        type="button"
                                        className="text-[12px] underline text-gray-600 hover:text-gray-800"
                                        onClick={() =>
                                          patchLine(i, "showDiscount", false)
                                        }
                                      >
                                        Hide discount
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 h-11">
                                    <button
                                      type="button"
                                      className="px-3.5 py-2 rounded-lg border text-sm hover:bg-gray-50"
                                      onClick={() =>
                                        patchLine(i, "showDiscount", true)
                                      }
                                    >
                                      Add discount
                                    </button>
                                    {hasDiscount && (
                                      <span
                                        className="text-[12px] px-2 py-1 rounded-full border bg-gray-50 text-gray-700"
                                        title="Click to edit"
                                        onClick={() =>
                                          patchLine(i, "showDiscount", true)
                                        }
                                        style={{ cursor: "pointer" }}
                                      >
                                        −
                                        {Number(li.discountAmount) > 0
                                          ? money(li.discountAmount, currency)
                                          : `${li.discountPercent}%`}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>

                              <LabeledInput
                                label="Tax %"
                                type="text"
                                inputMode="decimal"
                                pattern="^[0-9]*[.]?[0-9]*$"
                                value={String(li.taxPercent ?? "")}
                                onChange={(e) =>
                                  patchLine(i, "taxPercent", Number(e.target.value))
                                }
                                rightAdornment={<Percent size={14} />}
                              />
                              <div className="pt-7">
                                <LabeledCheckbox
                                  label="Tax incl."
                                  checked={!!li.isTaxInclusive}
                                  onChange={(e) =>
                                    patchLine(i, "isTaxInclusive", e.target.checked)
                                  }
                                />
                              </div>
                            </div>

                            {/* Description + mini totals */}
                            <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-3">
                              <div className="lg:col-span-6">
                                <label className={LABEL_BASE}>Description</label>
                                <textarea
                                  rows={2}
                                  className={INPUT_BASE + " h-auto"}
                                  placeholder="Describe the product or service"
                                  value={li.description}
                                  onChange={(e) =>
                                    patchLine(i, "description", e.target.value)
                                  }
                                />
                              </div>

                              {/* wider totals columns + no overlap */}
                              <div className="lg:col-span-6 grid grid-cols-3 md:grid-cols-6 gap-4 text-[13px] leading-tight">
                                <div className="col-span-1 md:col-span-2 min-w-0">
                                  <div className="text-gray-500">Subtotal</div>
                                  <div className="font-semibold tabular-nums whitespace-nowrap overflow-hidden text-ellipsis pr-3">
                                    {money(c.subtotal, currency)}
                                  </div>
                                </div>
                                <div className="col-span-1 md:col-span-2 min-w-0">
                                  <div className="text-gray-500">Tax</div>
                                  <div className="font-semibold tabular-nums whitespace-nowrap overflow-hidden text-ellipsis">
                                    {money(c.taxAmount, currency)}
                                  </div>
                                </div>
                                <div className="col-span-1 md:col-span-2 min-w-0">
                                  <div className="text-gray-500">Line Total</div>
                                  <div className="font-semibold tabular-nums whitespace-nowrap overflow-hidden text-ellipsis">
                                    {money(c.total, currency)}
                                  </div>
                                </div>
                                <div className="col-span-3 md:col-span-6 flex items-end justify-end">
                                  <div className={HINT_BASE + " whitespace-nowrap"}>
                                    Amount first, then % (if both set, amount wins).
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="flex justify-between items-center pt-1">
                      <div className={HINT_BASE + " flex items-center gap-1"}>
                        <Info size={14} />
                        Use ↑/↓ to re-order. Totals update instantly.
                      </div>
                      <button
                        type="button"
                        className="px-3.5 py-2.5 rounded-xl border text-sm hover:bg-gray-50"
                        onClick={addLine}
                      >
                        Add another line
                      </button>
                    </div>
                  </Section>

                  {/* Notes */}
                  <Section
                    icon={FileText}
                    title="Notes"
                    hint="Shown on the invoice PDF & emails"
                  >
                    <textarea
                      rows={3}
                      className={INPUT_BASE + " h-auto"}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Optional message / terms / thank you note"
                    />
                  </Section>
                </div>

                {/* Sidebar summary */}
                <div className="space-y-6">
                  <Section
                    icon={DollarSign}
                    title="Summary"
                    hint="Line discounts and totals"
                  >
                    <div className="mt-2 space-y-2 text-[15px]">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subtotal</span>
                        <span className="font-semibold tabular-nums whitespace-nowrap">
                          {money(totals.subtotal, currency)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Line discounts</span>
                        <span className="font-semibold tabular-nums whitespace-nowrap">
                          -{money(totals.lineDiscountTotal, currency)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tax</span>
                        <span className="font-semibold tabular-nums whitespace-nowrap">
                          {money(totals.taxTotal, currency)}
                        </span>
                      </div>
                      <div className="h-px bg-gray-200 my-2" />
                      <div className="flex justify-between text-lg">
                        <span className="font-semibold text-gray-800">Total</span>
                        <span className="font-extrabold tabular-nums whitespace-nowrap">
                          {money(totals.totalAmount, currency)}
                        </span>
                      </div>
                    </div>
                  </Section>
                </div>
              </div>
            </form>

            {/* Sticky Footer */}
            <div className="px-8 py-4 bg-white border-t flex flex-wrap justify-end gap-3 shadow-lg rounded-b-2xl">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2 rounded-lg text-gray-700 font-medium hover:bg-gray-100 border border-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => submit({ closeAfter: false })}
                className="px-5 py-2 bg-blue-50 text-blue-700 rounded-lg font-semibold hover:bg-blue-100 border border-blue-200"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => submit({ closeAfter: true })}
                className="px-7 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 shadow-sm"
              >
                Save & Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
